import { FC, useMemo } from 'react'
import * as styles from 'components/features/socialFeed/FeedUserCounters/FeedUserCounters.styl'
import cn from 'classnames'
import * as isNumber from 'lodash/isNumber'
import * as compact from 'lodash/compact'
import { createPluralFormatter } from 'helpers/formatHelpers'
import * as numeral from 'numeral'

type Counter = Pick<FeedUserCouterProps, 'counter' | 'copyFormatter'>

type FeedUserCounterMode = 'compact' | 'sparse'

type FeedUserCouterProps = {
  mode: FeedUserCounterMode
  counter: number
  copyFormatter: ReturnType<typeof createPluralFormatter>
}


const FeedUserCounter: FC<FeedUserCouterProps> = (props) => {
  const { mode, counter, copyFormatter } = props

  const rootClassName = cn(styles.feedUserCounter, {
    [styles[mode]]: true,
  })

  return (
    <div className={ rootClassName }>
      <div className={ styles.counter }>{ numeral(counter).format('0a') }</div>
      <div className={ styles.copy }>{ copyFormatter(counter) }</div>
    </div>
  )
}


const wrapWithTernary = (counter: FeedUserCouterProps['counter'] | undefined, copyFormatter: FeedUserCouterProps['copyFormatter']): Counter | null => {
  return isNumber(counter) ? { counter, copyFormatter } : null
}

type FeedUserCountersProps = {
  mode: FeedUserCounterMode
  postsCount?: number
  followersCount?: number
  likesCount?: number
}

export const FeedUserCounters: FC<FeedUserCountersProps> = (props) => {
  const { mode, postsCount, followersCount, likesCount } = props

  const sectionsArray: Counter[] = useMemo(() => {

    return compact([
      wrapWithTernary(postsCount, createPluralFormatter('Post', 'Posts')),
      wrapWithTernary(followersCount, createPluralFormatter('Follower', 'Followers')),
      wrapWithTernary(likesCount, createPluralFormatter('Like', 'Likes')),
    ]) as Exclude<ReturnType<typeof wrapWithTernary>, null>[]

  }, [postsCount, followersCount, likesCount])

  const sectionChildren = useMemo(() => {
    return sectionsArray.map(({ counter, copyFormatter }, idx) => (
      <div className={ styles.section } key={ idx }>
        <FeedUserCounter
          mode={ mode }
          counter={ counter }
          copyFormatter={ copyFormatter }
        />
      </div>
    ))
  }, [sectionsArray, mode])

  const rootClassName = cn(styles.feedUserCounters, {
    [styles[mode]]: true,
  })

  if (!isNumber(postsCount) && !isNumber(followersCount) && !isNumber(likesCount)) {
    return null
  }

  return (
    <div className={ rootClassName }>
      { sectionChildren }
    </div>
  )
}
